import { useRef, useState, type ReactNode } from 'react'
import { useDismiss } from '../../lib/hooks'
import type { HeadingLevel } from '../../lib/types'
import type { FormatState } from './format'

export interface SelectionBox {
  top: number
  bottom: number
  left: number
  width: number
}

/** What the block holding the selection is now, for the Heading and Quote toggles. */
export type BlockKind = { type: 'text' } | { type: 'heading'; level: HeadingLevel } | { type: 'pullquote'; level: HeadingLevel }

export type FormatAction = 'bold' | 'italic' | 'highlight'

/** Where a bar over the selection sits: fixed to the viewport, below the selection on touch screens (the system's menu is above). */
function barPosition(box: SelectionBox, half = 240) {
  const coarse = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const center = Math.min(Math.max(box.left + box.width / 2, half), Math.max(half, vw - half))
  const top = coarse ? box.bottom + 12 : Math.max(60, box.top - 44)
  return { top, left: center }
}

/**
 * The bar over selected words (Niki, 10/1–10/2): Bold · Italic · Highlight ·
 * Link, the block's Heading 1/2/3 and Quote (“), then Margin note, which
 * highlights the words and opens a note about them beside the paragraph.
 * Heading and Quote turn the whole paragraph. While Quote is on, 1/2/3 size
 * the quote instead and it stays a centred quote; pressing Quote again turns
 * it back into a paragraph, as pressing the heading that's on does.
 */
export function SelectionToolbar({
  box,
  format,
  block,
  link,
  canNote,
  linkOnly = false,
  onFormat,
  onBlock,
  onLink,
  onNote,
}: {
  box: SelectionBox
  format: FormatState
  block: BlockKind
  /** The link the words are in, if any. */
  link: string | null
  /** A margin note needs words that aren't already a note's (or an unfold's). */
  canNote: boolean
  /** Words in a margin note: plain words that can take a link, so the bar is Link alone. */
  linkOnly?: boolean
  onFormat: (action: FormatAction) => void
  onBlock: (to: BlockKind) => void
  onLink: () => void
  onNote: () => void
}) {
  const { top, left: center } = barPosition(box, linkOnly ? 24 : 240)
  const quote = block.type === 'pullquote'
  const level = block.type === 'text' ? null : block.level
  const heading = (l: HeadingLevel) => (
    <Btn
      key={l}
      label={quote ? `Quote size ${l}` : `Heading ${l}`}
      pressed={level === l}
      onClick={() => {
        if (quote) onBlock({ type: 'pullquote', level: l })
        else onBlock(level === l ? { type: 'text' } : { type: 'heading', level: l })
      }}
    >
      <Glyph size={HEADING_GLYPH[l]}>H{l}</Glyph>
    </Btn>
  )
  const linkBtn = (
    <Btn label={link ? 'Edit link' : 'Link'} keys="Meta+K Control+K" pressed={!!link} onClick={onLink}>
      <LinkGlyph />
    </Btn>
  )
  if (linkOnly) {
    return (
      <div
        role="toolbar"
        aria-label="Selected words"
        data-keep-selection
        onMouseDown={(e) => e.preventDefault()}
        style={{ top, left: center }}
        className="fixed z-40 flex -translate-x-1/2 items-center gap-0.5 rounded-lg bg-foreground p-1 font-sans text-[13px] font-medium text-card shadow-[var(--shadow-chip)]"
      >
        {linkBtn}
      </div>
    )
  }
  return (
    <div
      role="toolbar"
      aria-label="Selected words"
      data-keep-selection
      onMouseDown={(e) => e.preventDefault()}
      style={{ top, left: center }}
      className="fixed z-40 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 items-center gap-0.5 overflow-x-auto rounded-lg bg-foreground p-1 font-sans text-[13px] font-medium text-card shadow-[var(--shadow-chip)]"
    >
      <Btn label="Bold" keys="Meta+B Control+B" pressed={format.bold} onClick={() => onFormat('bold')}>
        <Glyph className="font-bold">B</Glyph>
      </Btn>
      <Btn label="Italic" keys="Meta+I Control+I" pressed={format.italic} onClick={() => onFormat('italic')}>
        <Glyph className="pr-px italic">I</Glyph>
      </Btn>
      {/* A pull quote is already the loudest thing on the page: no highlighter in it. */}
      {!quote && (
        <Btn label="Highlight" pressed={format.highlight} onClick={() => onFormat('highlight')}>
          <span aria-hidden className="size-4 rounded-[4px] bg-accent/70" />
        </Btn>
      )}
      {linkBtn}
      <Divider />
      {([1, 2, 3] as const).map(heading)}
      <Btn
        label="Quote"
        pressed={quote}
        onClick={() => onBlock(quote ? { type: 'text' } : { type: 'pullquote', level: level ?? 2 })}
      >
        <Glyph size="text-[22px]" className="translate-y-[4px]">“</Glyph>
      </Btn>
      {canNote && (
        <>
          <Divider />
          <button
            type="button"
            onClick={onNote}
            title="Highlight these words and write a note about them in the margin"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-[5px] px-[9px] py-[5px] hover:bg-card/10"
          >
            <NoteGlyph />
            Margin note
          </button>
        </>
      )}
    </div>
  )
}

function Btn({
  label,
  title,
  keys,
  pressed,
  onClick,
  children,
}: {
  label: string
  title?: string
  keys?: string
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      aria-keyshortcuts={keys}
      title={title ?? label}
      onClick={onClick}
      className={`flex h-7 min-w-[30px] items-center justify-center whitespace-nowrap rounded-[5px] px-[7px] ${pressed ? 'bg-card/20 text-card' : 'hover:bg-card/10'}`}
    >
      {children}
    </button>
  )
}

/** H1–H3 drawn at stepped sizes, so the buttons show which way the size goes. */
const HEADING_GLYPH: Record<HeadingLevel, string> = { 1: 'text-[18px]', 2: 'text-[14.5px]', 3: 'text-[11.5px]' }

/** A button's letterform: one face, size and line box, so B, I and H1–H3 sit on the same centre. */
function Glyph({ size = 'text-[15px]', className = '', children }: { size?: string; className?: string; children: ReactNode }) {
  return <span aria-hidden className={`block font-display leading-none ${size} ${className}`}>{children}</span>
}

/** Paste or type a web address for the selected words; empty (or Remove) takes the link off. */
export function LinkEditor({ box, initial, onApply, onCancel }: { box: SelectionBox; initial: string; onApply: (url: string | null) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLFormElement>(null)
  // A click elsewhere or Esc leaves the words as they were.
  useDismiss(ref, true, onCancel)
  const { top, left } = barPosition(box)
  return (
    <form
      ref={ref}
      role="dialog"
      aria-label="Link"
      data-keep-selection
      onSubmit={(e) => {
        e.preventDefault()
        onApply(value.trim() || null)
      }}
      style={{ top, left }}
      className="fixed z-40 flex w-[min(380px,calc(100vw-1rem))] -translate-x-1/2 items-center gap-1 rounded-lg bg-foreground p-1 font-sans text-[13px] font-medium text-card shadow-[var(--shadow-chip)]"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        inputMode="url"
        placeholder="Paste a link"
        aria-label="Web address"
        className="h-7 min-w-0 flex-1 rounded-[5px] bg-card/10 px-2 text-[13px] text-card outline-none placeholder:text-card/50"
      />
      {initial && (
        <button type="button" onClick={() => onApply(null)} className="h-7 whitespace-nowrap rounded-[5px] px-2 hover:bg-card/10">
          Remove
        </button>
      )}
      <button type="submit" className="h-7 whitespace-nowrap rounded-[5px] bg-card/20 px-2.5 hover:bg-card/25">
        {initial ? 'Save' : 'Link'}
      </button>
    </form>
  )
}

function LinkGlyph() {
  return (
    <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

/** A small card with a line under it: words, and a note beside them. */
function NoteGlyph() {
  return (
    <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
      <path d="M4 19h9" />
      <rect x="14" y="4" width="7" height="9" rx="1.5" />
      <path d="M4 14h6" />
    </svg>
  )
}

function Divider() {
  return <span aria-hidden className="mx-0.5 w-px self-stretch bg-card/20" />
}
